/**
 * Auth API client — talks to the `apps/api` backend (`/api/v1/auth/*`).
 *
 * Mirrors the backend's `{ success, data }` envelope and Bearer-token model:
 * login/register return `{ user, tokens }`, and authenticated calls send
 * `Authorization: Bearer <accessToken>`. The base URL defaults to `/api/v1`,
 * which the Vite dev server proxies to the backend (see vite.config.ts).
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1'

export interface AuthUser {
  id: string
  email: string
  name: string
  avatar?: string | null
  role: string
  emailVerified?: boolean
  createdAt?: string
}

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresIn: number
  tokenType: string
}

export interface AuthResult {
  user: AuthUser
  tokens: AuthTokens
}

/** Thrown on a non-2xx response; `message` is the backend's error message. */
export class AuthApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message)
    this.name = 'AuthApiError'
  }
}

async function request<T>(
  path: string,
  init: { method: string; body?: unknown; token?: string }
): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method: init.method,
      headers: {
        'Content-Type': 'application/json',
        ...(init.token ? { Authorization: `Bearer ${init.token}` } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })
  } catch {
    throw new AuthApiError('Network error — is the backend running?', 0)
  }

  const json = (await res.json().catch(() => ({}))) as {
    data?: unknown
    error?: { code?: string; message?: string }
  }

  if (!res.ok) {
    const raw = json.error?.message
    // Backend validation errors arrive as a stringified ZodError; show a
    // friendly fallback rather than the JSON blob.
    const message =
      typeof raw === 'string' && !raw.trimStart().startsWith('[')
        ? raw
        : res.status === 401
          ? 'Invalid email or password'
          : `Request failed (${res.status})`
    throw new AuthApiError(message, res.status, json.error?.code)
  }

  return json.data as T
}

export const authApi = {
  login: (email: string, password: string) =>
    request<AuthResult>('/auth/login', { method: 'POST', body: { email, password } }),

  register: (email: string, password: string, name: string) =>
    request<AuthResult>('/auth/register', {
      method: 'POST',
      body: { email, password, confirmPassword: password, name },
    }),

  me: (token: string) => request<AuthUser>('/auth/me', { method: 'GET', token }),

  /** Exchange a refresh token for a fresh token pair (rotating). */
  refresh: (refreshToken: string) =>
    request<AuthTokens>('/auth/refresh', { method: 'POST', body: { refreshToken } }),

  logout: (refreshToken: string) =>
    request<null>('/auth/logout', { method: 'POST', body: { refreshToken } }),
}
